import { useState } from 'react';
import { useTranslations } from 'use-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { FiscalYear } from "@/types/fiscalYear";
import { MAX_FISCAL_YEAR_MONTHS, fiscalYearLengthInMonths } from "@/services/onboardingService";

interface FiscalYearEditDialogProps {
  fiscalYear: FiscalYear;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

/** broken-fiscal-year.EC4/EC5: client-side mirror of the backend's shape/order/legal-max checks —
 * same immediate feedback pattern as onboarding's CompanyContextForm. The backend
 * (`validateFiscalYearDates`) remains the enforced source of truth. */
function validate(
  startDate: string,
  endDate: string,
  t: ReturnType<typeof useTranslations<'consultantSettings.fiscalYear.editDialog'>>,
): string | null {
  if (!startDate || !endDate) return null;
  if (new Date(endDate) <= new Date(startDate)) {
    return t('endBeforeStart');
  }
  const months = fiscalYearLengthInMonths(startDate, endDate);
  if (months !== null && months > MAX_FISCAL_YEAR_MONTHS) {
    return t('tooLong', { months, max: MAX_FISCAL_YEAR_MONTHS });
  }
  return null;
}

/** broken-fiscal-year.F2: correct an already-open fiscal year's dates from Settings (e.g. a
 * company onboarded with the wrong default Jan–Dec dates instead of its real start date). */
export const FiscalYearEditDialog = ({ fiscalYear, open, onOpenChange, onSaved }: FiscalYearEditDialogProps) => {
  const t = useTranslations('consultantSettings.fiscalYear.editDialog');
  const [startDate, setStartDate] = useState(fiscalYear.startDate);
  const [endDate, setEndDate] = useState(fiscalYear.endDate);
  const [saving, setSaving] = useState(false);

  const fiscalYearService = ServiceRegistry.getFiscalYearService();
  const validationError = validate(startDate, endDate, t);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Reset to the loaded fiscal year's own values so a cancelled edit never leaks into a
      // later re-open of the same dialog instance.
      setStartDate(fiscalYear.startDate);
      setEndDate(fiscalYear.endDate);
    }
    onOpenChange(next);
  };

  const handleSave = async () => {
    if (validationError) return;

    try {
      setSaving(true);
      await fiscalYearService.updateFiscalYear({
        fiscalYearId: fiscalYear.id,
        startDate,
        endDate,
        expectedUpdatedAt: fiscalYear.updatedAt,
      });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating fiscal year:', error);
      // Error toast is already shown by the API client (EC1-EC3/EC6's friendly messages) —
      // the dialog stays open so the consultant can adjust the dates and retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="editStartDate">{t('startDate')}</Label>
            <Input
              id="editStartDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={saving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="editEndDate">{t('endDate')}</Label>
            <Input
              id="editEndDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={saving}
            />
          </div>
        </div>

        {validationError && (
          <Alert variant="destructive">
            <AlertDescription>{validationError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || !!validationError || !startDate || !endDate}>
            {saving ? t('saving') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
